<template>
  <div class="payment-form-wrapper">
    <div v-if="!compact" class="form-header mb-4">
      <h3 class="text-lg font-semibold">
        {{ title || $t("message.payment_form.add_payment") }}
        {{
          amount ? `(${$t("message.payment_form.pending")}: $${amount})` : ""
        }}
      </h3>
      <p v-if="description" class="text-sm text-gray-600 mt-1">
        {{ description }}
      </p>
    </div>

    <div class="form-content">
      <!-- Processor Selection -->
      <div class="form-group">
        <label class="form-label"
          >{{ $t("message.payment_form.payment_processor") }}
          {{ $t("message.payment_form.required_field") }}</label
        >
        <div class="processor-buttons">
          <button
            v-for="processor in processors"
            :key="processor.value"
            type="button"
            @click="selectProcessor(processor.value)"
            class="processor-btn"
            :class="{ selected: paymentData.processor === processor.value }"
          >
            <span class="processor-icon">{{ processor.icon }}</span>
            <span class="processor-name">{{
              $t(`message.payment_form.processors.${processor.labelKey}`)
            }}</span>
          </button>
        </div>
      </div>

      <!-- Amount (hidden, comes from props) -->
      <div v-if="showAmountField" class="form-group">
        <label class="form-label"
          >{{ $t("message.payment_form.amount") }}: ${{
            paymentData.amount
          }}</label
        >
        <input
          type="number"
          step="0.01"
          v-model="paymentData.amount"
          class="form-input"
        />
      </div>

      <!-- Transaction ID -->
      <div class="form-group">
        <label class="form-label">
          {{
            transactionIdRequired
              ? $t("message.payment_form.transaction_id")
              : $t("message.payment_form.transaction_id_optional")
          }}
          {{
            transactionIdRequired
              ? $t("message.payment_form.required_field")
              : ""
          }}
        </label>
        <input
          type="text"
          v-model="paymentData.transactionId"
          class="form-input"
          :placeholder="
            transactionIdPlaceholder ||
              $t('message.payment_form.enter_transaction_id')
          "
        />
      </div>

      <!-- Cardholder Name (only if showCardholderField is true) -->
      <div v-if="showCardholderField" class="form-group">
        <label class="form-label">{{
          $t("message.payment_form.cardholder_name")
        }}</label>
        <input
          type="text"
          v-model="paymentData.cardholderName"
          class="form-input"
          :placeholder="$t('message.payment_form.enter_cardholder_name')"
        />
      </div>

      <!-- Card Number (only if showCardField is true) -->
      <div v-if="showCardField" class="form-group">
        <label class="form-label">{{
          $t("message.payment_form.card_last_4")
        }}</label>
        <input
          type="text"
          v-model="paymentData.card"
          class="form-input"
          :placeholder="$t('message.payment_form.enter_last_4_digits')"
          maxlength="4"
        />
      </div>

      <!-- Action Buttons -->
      <div class="form-actions">
        <button
          v-if="showSubmitButton"
          @click="handleSubmit"
          class="action-btn btn-success"
          :disabled="!isValid || submitting"
          type="button"
        >
          <span v-if="submitting">{{
            submittingText || $t("message.payment_form.processing")
          }}</span>
          <span v-else>{{
            submitText || $t("message.payment_form.confirm_payment")
          }}</span>
        </button>
        <button
          v-if="showCancelButton"
          @click="handleCancel"
          class="action-btn btn-secondary"
          :disabled="submitting"
          type="button"
        >
          {{ cancelText || $t("message.payment_form.cancel") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PaymentForm",
  props: {
    // Payment data
    amount: {
      type: [Number, String],
      default: 0
    },
    processor: {
      type: String,
      default: ""
    },
    transactionId: {
      type: String,
      default: ""
    },
    cardholderName: {
      type: String,
      default: ""
    },
    card: {
      type: String,
      default: ""
    },
    date: {
      type: String,
      default: ""
    },
    // UI customization
    title: {
      type: String,
      default: ""
    },
    description: {
      type: String,
      default: ""
    },
    compact: {
      type: Boolean,
      default: false
    },
    showCardholderField: {
      type: Boolean,
      default: false
    },
    showCardField: {
      type: Boolean,
      default: false
    },
    showAmountField: {
      type: Boolean,
      default: true
    },
    transactionIdPlaceholder: {
      type: String,
      default: ""
    },
    transactionIdRequired: {
      type: Boolean,
      default: false
    },
    // Button customization
    showSubmitButton: {
      type: Boolean,
      default: true
    },
    showCancelButton: {
      type: Boolean,
      default: true
    },
    submitText: {
      type: String,
      default: ""
    },
    submittingText: {
      type: String,
      default: ""
    },
    cancelText: {
      type: String,
      default: ""
    },
    submitting: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      processors: [
        { value: "clover", labelKey: "clover", icon: "🍀" },
        { value: "stripe", labelKey: "stripe", icon: "💳" },
        { value: "cash", labelKey: "cash", icon: "💵" },
        { value: "check", labelKey: "check", icon: "📝" },
        { value: "other", labelKey: "other", icon: "💼" },
        { value: "Paypal", labelKey: "paypal", icon: "🅿️" },
        { value: "Credit Card", labelKey: "credit_card", icon: "💳" }
      ],
      paymentData: {
        processor: this.processor,
        amount: this.amount,
        transactionId: this.transactionId,
        cardholderName: this.cardholderName || "Manual Entry",
        card: this.card || ""
      }
    };
  },
  computed: {
    isValid() {
      return (
        this.paymentData.processor &&
        this.paymentData.amount > 0 &&
        (!this.transactionIdRequired || this.paymentData.transactionId)
      );
    }
  },
  watch: {
    amount(newVal) {
      this.paymentData.amount = newVal;
    },
    processor(newVal) {
      this.paymentData.processor = newVal;
    },
    transactionId(newVal) {
      this.paymentData.transactionId = newVal;
    },
    cardholderName(newVal) {
      this.paymentData.cardholderName = newVal;
    },
    card(newVal) {
      this.paymentData.card = newVal;
    },
    date(newVal) {
      this.paymentData.date = newVal;
    }
  },
  methods: {
    getTodayDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    selectProcessor(value) {
      this.paymentData.processor = value;
      this.$emit("processor-changed", value);
    },
    handleSubmit() {
      if (!this.isValid) return;

      const paymentData = {
        paymentType: this.paymentData.processor,
        transactionId: this.paymentData.transactionId || `MANUAL-${Date.now()}`,
        amount: parseFloat(this.paymentData.amount),
        dateTransaction: this.getTodayDate(),
        cardholderName: this.paymentData.cardholderName,
        card: this.paymentData.card
      };

      this.$emit("submit", paymentData);
    },
    handleCancel() {
      this.$emit("cancel");
    },
    reset() {
      this.paymentData = {
        processor: "",
        amount: this.amount,
        transactionId: "",
        cardholderName: "Manual Entry",
        card: ""
      };
    }
  }
};
</script>

<style scoped>
.payment-form-wrapper {
  width: 100%;
}

.form-header {
  margin-bottom: 1rem;
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.form-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  display: block;
}

.form-input {
  width: 100%;
  padding: 0.375rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  font-size: 0.8rem;
}

.form-input:focus {
  outline: none;
  box-shadow: 0 0 0 2px #3b82f6;
}

.form-input:disabled,
.form-input:read-only {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

.processor-buttons {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.375rem;
}

@media (max-width: 768px) {
  .processor-buttons {
    grid-template-columns: repeat(3, 1fr);
  }
}

.processor-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  padding: 0.5rem 0.25rem;
  border: 2px solid #d1d5db;
  border-radius: 0.25rem;
  transition: all 200ms;
  background-color: white;
  cursor: pointer;
}

.processor-btn:hover {
  border-color: #60a5fa;
  background-color: #eff6ff;
}

.processor-btn.selected {
  border-color: #2563eb;
  background-color: #dbeafe;
}

.processor-icon {
  font-size: 1.25rem;
}

.processor-name {
  font-size: 0.65rem;
  font-weight: 600;
  text-align: center;
  line-height: 1;
}

.form-actions {
  display: flex;
  gap: 0.375rem;
  margin-top: 0.5rem;
}

.action-btn {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
  font-weight: 600;
  font-size: 0.875rem;
  transition: colors 200ms;
  border: none;
  cursor: pointer;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-success {
  background-color: #16a34a;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background-color: #15803d;
}

.btn-secondary {
  background-color: #d1d5db;
  color: #374151;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #9ca3af;
}
</style>
